import { useState, useRef, useEffect } from "react";
import { Task, ViewType } from "../types";
import {
  pickInitialFocus,
  pickNearestInDirection,
  type Direction,
  type NavCandidate,
} from "../lib/spatialNavigation";
import { prefersReducedMotion } from "../lib/prefersReducedMotion";
import { useKeyboardShortcuts } from "./useKeyboardShortcuts";

// What the arrow keys stop on: every task card, and each list title that
// opens and closes its list.
const NAV_STOP_SELECTOR = "[data-task-id], [data-list-toggle]";

export interface UseBoardNavigationArgs {
  visibleTasks: Task[];
  viewType: ViewType;
  isNoDialogOpen: () => boolean;
  isMobile: boolean;
  activeDragTask: Task | null;
  openTask: (taskId: string) => void;
}

export interface UseBoardNavigationResult {
  focusedTaskId: string | null;
}

const ARROW_DIRECTIONS: [key: string, direction: Direction][] = [
  ["ArrowUp", "up"],
  ["ArrowDown", "down"],
  ["ArrowLeft", "left"],
  ["ArrowRight", "right"],
];

// Where a stop sits, for choosing the next one. A list title is measured by
// its whole header row: the title itself is narrow, so its centre sits far
// left of the cards' centres, and the arrows took it for something to the
// left of its first card rather than above it.
function navRect(node: HTMLElement): DOMRect {
  const header = node.dataset.listToggle
    ? node.closest<HTMLElement>("[data-list-header]")
    : null;
  return (header ?? node).getBoundingClientRect();
}

// The task whose card holds keyboard focus right now, read from the DOM so
// Enter and Escape act on what is focused at the moment of the key press.
function focusedCardTaskId(): string | null {
  const active = document.activeElement;
  return active instanceof HTMLElement ? active.dataset.taskId ?? null : null;
}

export function useBoardNavigation({
  visibleTasks,
  viewType,
  isNoDialogOpen,
  isMobile,
  activeDragTask,
  openTask,
}: UseBoardNavigationArgs): UseBoardNavigationResult {
  // The task card the keyboard is on, which shows the focus ring. The ring
  // is keyboard focus made visible and nothing else: it goes wherever focus
  // goes, and leaves a card as soon as focus does — Tab to a button, a click,
  // a dialog opening. A ring that stayed behind while focus moved on made
  // Enter open the ringed card instead of pressing the button the user had
  // tabbed to. (TaskCard also paints the ring only while its card holds
  // focus, so a card removed with focus on it can't bring a ring back.)
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);
  // Whether the last input was the keyboard. A click focuses the card it
  // lands on, and a mouse user shouldn't see a ring for that — the same rule
  // browsers apply to :focus-visible, kept here so it also holds for the
  // focus the arrow keys and a closing dialog move by script.
  const keyboardInputRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // A browser or system chord (Cmd+Tab to switch apps) isn't moving
      // around the page.
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      keyboardInputRef.current = true;
      // A card focused by a click shows its ring once the keyboard is used.
      const taskId = focusedCardTaskId();
      if (taskId) setFocusedTaskId(taskId);
    };
    const onPointerDown = () => {
      keyboardInputRef.current = false;
    };
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      const taskId =
        target instanceof HTMLElement ? target.dataset.taskId : undefined;
      setFocusedTaskId(taskId && keyboardInputRef.current ? taskId : null);
    };
    // Focus moving to another element is settled by that element's focusin.
    // With nothing to move to, focus is either going to the page itself (a
    // click on empty space), which takes the ring away, or the whole window
    // is losing focus (switching apps), which leaves focus where it was.
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget) return;
      const target = event.target;
      setTimeout(() => {
        if (document.activeElement !== target) setFocusedTaskId(null);
      });
    };
    // Capture phase, so the input kind is known before any shortcut handler
    // moves focus in response to the same key.
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // Arrow-key board navigation is desktop-only (mobile uses the carousel),
  // suspended while a dialog is open, a drag is in flight, or a popover
  // menu (the task-card "more" menu) is open — that menu does its own
  // arrow-key navigation between items and we don't want to shadow it.
  const isBoardNavEnabled = () =>
    viewType === "board" &&
    isNoDialogOpen() &&
    !isMobile &&
    !activeDragTask &&
    document.querySelector('[role="menu"]') === null;

  // Reads the stops straight from the DOM, so the focus jump always reflects
  // the current masonry layout — no need to mirror the column structure into
  // React state. The move starts from wherever keyboard focus is: a stop, the
  // card around a focused control, the title of the list header around a
  // focused control (its + button, say), or any other focused element by its
  // position. The + button counts as its title because it sits at the right
  // edge, far off the cards' centre line: measured by itself, the arrow down
  // missed the list's first card. With nothing focused it starts at the
  // top-left stop, as it does when nothing lies that way from a focused
  // element outside the stops.
  const moveBoardFocus = (direction: Direction) => {
    const shownTaskIds = new Set(visibleTasks.map((t) => t.id));
    const stops = Array.from(
      document.querySelectorAll<HTMLElement>(NAV_STOP_SELECTOR),
    ).filter(
      // A card the filter hides stays in the DOM while it animates out.
      (node) => !node.dataset.taskId || shownTaskIds.has(node.dataset.taskId),
    );
    if (stops.length === 0) return;
    const candidates: NavCandidate[] = stops.map((node, i) => ({
      id: String(i),
      rect: navRect(node),
    }));

    const active = document.activeElement;
    const focusedElement =
      active instanceof HTMLElement && active !== document.body ? active : null;
    const originStop =
      focusedElement?.closest<HTMLElement>(NAV_STOP_SELECTOR) ??
      focusedElement
        ?.closest("[data-list-header]")
        ?.querySelector<HTMLElement>("[data-list-toggle]");
    const originIndex = originStop ? stops.indexOf(originStop) : -1;
    let nextId: string | null;
    if (originIndex !== -1) {
      nextId = pickNearestInDirection(candidates, String(originIndex), direction);
    } else {
      const origin = focusedElement && {
        id: "origin",
        rect: focusedElement.getBoundingClientRect(),
      };
      nextId =
        (origin &&
          pickNearestInDirection([...candidates, origin], origin.id, direction)) ??
        pickInitialFocus(candidates);
    }
    if (nextId === null) return;

    const node = stops[Number(nextId)];
    keyboardInputRef.current = true;
    // tabIndex={-1} lets a card take focus from script while staying out of
    // the Tab order; the ring, not an outline, is what shows it.
    node.focus({ preventScroll: true });
    setFocusedTaskId(node.dataset.taskId ?? null);
    node.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  // Escape: step out of the board — focus and ring leave the card together.
  const clearBoardFocus = () => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.dataset.taskId) active.blur();
    setFocusedTaskId(null);
  };

  // Drop the ring once its card is no longer on screen — filtered out,
  // completed and hidden, deleted, or the user left the board entirely. A
  // card the filter hides stays in the DOM and could keep focus unseen, so
  // focus leaves it too — also when a click gave it focus and it has no
  // ring, or Enter would open a task the user can't see.
  useEffect(() => {
    const focusedCard = document.activeElement;
    const focusedId = focusedCardTaskId();
    const isShown = (taskId: string) =>
      viewType === "board" && visibleTasks.some((t) => t.id === taskId);
    if (focusedId && !isShown(focusedId) && focusedCard instanceof HTMLElement) {
      focusedCard.blur();
    }
    if (
      focusedTaskId &&
      (focusedId !== focusedTaskId || !isShown(focusedTaskId))
    ) {
      setFocusedTaskId(null);
    }
  }, [focusedTaskId, visibleTasks, viewType]);

  useKeyboardShortcuts([
    // Escape — clear board keyboard focus (runs before the global Escape-clear-search)
    {
      key: "Escape",
      when: () =>
        isNoDialogOpen() &&
        focusedCardTaskId() !== null &&
        document.querySelector('[role="menu"]') === null,
      run: clearBoardFocus,
    },

    // Arrow keys — spatial navigation between the cards and collapsed lists
    ...ARROW_DIRECTIONS.map(([key, direction]) => ({
      key,
      when: isBoardNavEnabled,
      run: () => moveBoardFocus(direction),
    })),

    // Enter — open the task whose card has focus. With focus anywhere else
    // (a list's title or + button), Enter is left to that control.
    {
      key: "Enter",
      when: () => isBoardNavEnabled() && focusedCardTaskId() !== null,
      run: () => {
        const taskId = focusedCardTaskId();
        if (taskId) openTask(taskId);
      },
    },
  ]);

  return { focusedTaskId };
}
